import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

interface Script {
  id: string;
  topicId: string;
  variant: 'A' | 'B';
  scriptText: string;
  caption: string | null;
  hashtags: string[];
  seoTitle: string | null;
  seoDescription: string | null;
  charCount: number;
  wordCount: number;
  isApproved: boolean;
  rejectionNote: string | null;
}

interface Topic {
  id: string;
  title: string;
  abTest: boolean;
  status: string;
}

export default function ScriptReview() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('pending');
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  // Per-script avatar override chosen via the preview modal.
  const [pickedAvatars, setPickedAvatars] = useState<Record<string, { id: string; name: string }>>({});
  const [avatarModalFor, setAvatarModalFor] = useState<string | null>(null);

  const load = async () => {
    const [s, t] = await Promise.all([api.get('/scripts'), api.get('/topics')]);
    setScripts(s.data.scripts);
    setTopics(t.data.topics);
  };

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    load().catch(() => {});
    const id = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(id);
  }, [user, navigate]);

  const topicOf = (id: string) => topics.find((t) => t.id === id);
  const counts = {
    all: scripts.length,
    pending: scripts.filter(s => !s.isApproved).length,
    approved: scripts.filter(s => s.isApproved).length,
  };

  const filtered = scripts.filter((s) =>
    filter === 'all' ? true : filter === 'pending' ? !s.isApproved : s.isApproved
  );

  async function approve(id: string) {
    setBusy(id);
    try {
      // Approve, then immediately enqueue video generation and jump to /videos.
      // Users expect "Одобрить" = "одобрить и сделать видео"; the two-step
      // form (just-approve, generate-later) is still available via the
      // "Одобрены" tab → 🎬 button on already-approved rows.
      await api.post(`/scripts/${id}/approve`);
      const r = await api.post('/videos/generate', { scriptIds: [id], avatarId: pickedAvatars[id]?.id });
      toast.success(`Скрипт одобрен · видео в очереди (${r.data.queued} шт.)`);
      setTimeout(() => navigate('/videos'), 900);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error
                  ?? 'Ошибка одобрения / генерации';
      toast.error(msg);
      load();
    } finally { setBusy(null); }
  }

  async function reject(id: string) {
    const note = prompt('Причина отклонения:');
    if (!note) return;
    setBusy(id);
    try { await api.post(`/scripts/${id}/reject`, { note }); load(); }
    finally { setBusy(null); }
  }

  async function saveEdit(id: string) {
    const text = editing[id];
    if (text === undefined) return;
    setBusy(id);
    try {
      await api.put(`/scripts/${id}`, { scriptText: text });
      setEditing((e) => { const next = { ...e }; delete next[id]; return next; });
      toast.success('Сохранено');
      load();
    } finally { setBusy(null); }
  }

  async function regenerate(id: string) {
    setBusy(id);
    try { await api.post(`/scripts/${id}/regenerate`); toast.info('Перегенерируется…'); load(); }
    finally { setBusy(null); }
  }

  async function generateVideo(scriptId: string) {
    setBusy(scriptId);
    try {
      const r = await api.post('/videos/generate', { scriptIds: [scriptId], avatarId: pickedAvatars[scriptId]?.id });
      toast.success(`Видео в очереди · ${r.data.queued} шт.`);
      setTimeout(() => navigate('/videos'), 1200);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error
                  ?? 'Ошибка запуска генерации';
      toast.error(msg);
    } finally { setBusy(null); }
  }

  // Group by topic
  const byTopic = filtered.reduce<Record<string, Script[]>>((acc, s) => {
    (acc[s.topicId] ??= []).push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-16">

        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Проверка скриптов</h1>
          <p className="text-slate-500 mt-1.5">Одобри текст — получишь видео. Можешь редактировать прямо здесь.</p>
        </header>

        {/* Filter tabs */}
        <div className="inline-flex card-glass p-1 mb-6 gap-1">
          {([
            { key: 'pending',  label: 'На проверку', n: counts.pending },
            { key: 'approved', label: 'Одобрены',    n: counts.approved },
            { key: 'all',      label: 'Все',         n: counts.all },
          ] as const).map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === f.key
                  ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md'
                  : 'text-slate-600 hover:bg-white/60'
              }`}
            >
              {f.label}
              <span className="ml-2 opacity-70 text-xs">{f.n}</span>
            </button>
          ))}
        </div>

        {Object.keys(byTopic).length === 0 && (
          <div className="card-glass p-12 text-center">
            <div className="text-5xl mb-3">✍️</div>
            <h3 className="font-semibold mb-1">Пока нет скриптов</h3>
            <p className="text-sm text-slate-500 mb-4">Иди в <b>Темы</b> → добавь тему → нажми «Сгенерировать скрипты».</p>
            <button onClick={() => navigate('/topics')} className="btn btn-md btn-primary">
              📋 К темам
            </button>
          </div>
        )}

        {Object.entries(byTopic).map(([topicId, list]) => {
          const topic = topicOf(topicId);
          const sorted = list.sort((a, b) => a.variant.localeCompare(b.variant));
          return (
            <article key={topicId} className="card-glass p-6 mb-4">
              <h2 className="text-xl font-semibold mb-4">{topic?.title ?? '(тема удалена)'}</h2>
              <div className={sorted.length > 1 ? 'grid md:grid-cols-2 gap-4' : ''}>
                {sorted.map((s) => {
                  const isEditing = editing[s.id] !== undefined;
                  const stateBadge = s.isApproved
                    ? <span className="badge badge-green">✓ Одобрен</span>
                    : s.rejectionNote
                    ? <span className="badge badge-red">Отклонён</span>
                    : <span className="badge badge-amber">На проверку</span>;
                  return (
                    <div key={s.id} className="bg-white/50 rounded-2xl p-4 ring-1 ring-slate-200/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="badge badge-indigo">Вариант {s.variant}</span>
                          <span className="text-xs text-slate-500">{s.wordCount} слов · {s.charCount} симв</span>
                        </div>
                        {stateBadge}
                      </div>

                      <textarea
                        value={editing[s.id] ?? s.scriptText}
                        onChange={(e) => setEditing({ ...editing, [s.id]: e.target.value })}
                        rows={7}
                        className="textarea text-sm leading-relaxed"
                      />

                      {s.caption && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Caption</div>
                          <div className="text-xs bg-slate-50/80 p-3 rounded-xl text-slate-700">{s.caption}</div>
                        </div>
                      )}
                      {s.hashtags.length > 0 && (
                        <div className="mt-2 text-xs text-indigo-600 flex flex-wrap gap-1">
                          {s.hashtags.map((h, i) => <span key={i}>{h.startsWith('#') ? h : `#${h}`}</span>)}
                        </div>
                      )}
                      {s.rejectionNote && (
                        <div className="mt-3 text-xs text-rose-700 bg-rose-50 rounded-xl p-2">
                          ⚠ {s.rejectionNote}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mt-4">
                        <button
                          onClick={() => setAvatarModalFor(s.id)}
                          disabled={busy === s.id}
                          className="btn btn-sm btn-ghost"
                          title="Выбрать аватар для видео из этого скрипта"
                        >
                          🎭 {pickedAvatars[s.id] ? pickedAvatars[s.id]!.name.slice(0, 16) : 'Аватар'}
                        </button>
                        {isEditing && (
                          <button onClick={() => saveEdit(s.id)} disabled={busy === s.id} className="btn btn-sm btn-primary">
                            💾 Сохранить
                          </button>
                        )}
                        {!s.isApproved && !isEditing && (
                          <button onClick={() => approve(s.id)} disabled={busy === s.id} className="btn btn-sm btn-success">
                            ✓ Одобрить
                          </button>
                        )}
                        {s.isApproved && (
                          <button onClick={() => generateVideo(s.id)} disabled={busy === s.id} className="btn btn-sm btn-primary">
                            🎬 Создать видео
                          </button>
                        )}
                        {!s.isApproved && (
                          <button onClick={() => reject(s.id)} disabled={busy === s.id} className="btn btn-sm btn-ghost">
                            Отклонить
                          </button>
                        )}
                        <button onClick={() => regenerate(s.id)} disabled={busy === s.id} className="btn btn-sm btn-ghost ml-auto">
                          🔄 Регенерировать
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>

      {avatarModalFor && (
        <AvatarPickerModal
          onClose={() => setAvatarModalFor(null)}
          onPick={(a) => {
            setPickedAvatars((p) => ({ ...p, [avatarModalFor]: a }));
            setAvatarModalFor(null);
            toast.success(`Аватар выбран: ${a.name}`);
          }}
        />
      )}
    </div>
  );
}

interface HeyGenAvatarLite {
  avatar_id: string;
  avatar_name: string;
  preview_image_url?: string | null;
  gender?: string | null;
}

function AvatarPickerModal({
  onPick,
  onClose,
}: {
  onPick: (a: { id: string; name: string }) => void;
  onClose: () => void;
}) {
  const [avatars, setAvatars] = useState<HeyGenAvatarLite[] | null>(null);
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .get('/settings/heygen/avatars')
      .then((r) => setAvatars(r.data.avatars ?? []))
      .catch((e: unknown) =>
        setErr(
          (e as { response?: { data?: { error?: string } } }).response?.data?.error ??
            'Не удалось загрузить аватары (нужен валидный HeyGen ключ в /settings)'
        )
      );
  }, []);

  const list = (avatars ?? []).filter(
    (a) => !filter || a.avatar_name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-semibold text-lg">Выберите аватар</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">
            ✕
          </button>
        </div>
        <div className="p-4 pb-2">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="🔎 Поиск по имени…"
            className="input"
            autoFocus
          />
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {err && <div className="text-sm bg-rose-50 text-rose-700 rounded-xl p-3">{err}</div>}
          {!avatars && !err && (
            <div className="text-sm text-slate-500 p-4 text-center">Загружаю аватары HeyGen (~20с)…</div>
          )}
          {avatars && (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {list.slice(0, 120).map((a) => (
                <button
                  key={a.avatar_id}
                  type="button"
                  onClick={() => onPick({ id: a.avatar_id, name: a.avatar_name })}
                  title={`${a.avatar_name}\nID: ${a.avatar_id}`}
                  className="group relative rounded-xl overflow-hidden ring-2 ring-transparent hover:ring-indigo-400 transition-all"
                >
                  {a.preview_image_url ? (
                    <img
                      src={a.preview_image_url}
                      alt={a.avatar_name}
                      loading="lazy"
                      className="w-full aspect-[3/4] object-cover bg-slate-200"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full aspect-[3/4] bg-slate-200 flex items-center justify-center text-3xl">
                      🎭
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[10px] leading-tight px-1.5 py-1 text-left">
                    <div className="truncate font-medium">{a.avatar_name}</div>
                    <div className="opacity-70 truncate">{a.gender ?? '—'}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {avatars && list.length > 120 && (
            <div className="text-xs text-slate-400 mt-2">показаны первые 120 — уточните поиск</div>
          )}
          {avatars && list.length === 0 && (
            <div className="text-sm text-slate-500 p-4 text-center">Ничего не найдено</div>
          )}
        </div>
      </div>
    </div>
  );
}
